import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useProjectFolderContext } from "../folders/ProjectFoldersProvider";
import { HexColor } from "vega";
import { FieldFolderName } from "./fields/FieldFolderName";
import { FieldFolderColor } from "./fields/FieldFolderColor";

type Fields = {
  name: {
    value: string;
    isValid: boolean;
  };
  color: {
    value: HexColor | null;
    isValid: boolean;
  };
};

interface ModalCreateFolderProps {
  onClose: () => void;
}

/** Component that renders a modal for creating a new folder */
export const ModalCreateFolder = ({ onClose }: ModalCreateFolderProps) => {
  const createFolder = useProjectFolderContext((s) => s.createFolder);
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Fields>({
    name: {
      value: "",
      isValid: false,
    },
    color: {
      value: null,
      isValid: true,
    },
  });

  const handleFieldChange = <T extends keyof Fields>(
    fieldName: T,
    field: Fields[T],
  ) => {
    setFields((prevFields) => ({
      ...prevFields,
      [fieldName]: field,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const { error } = await createFolder({
      name: fields.name.value.trim(),
      color: fields.color.value,
    });
    setIsLoading(false);

    if (error !== undefined) {
      addUtilityToastFailure("Failed to create folder");
    }

    onClose();
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>Add New Folder</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm
          id={`create-folder-form`}
          component="form"
          role="form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <EuiFlexGroup>
            <EuiFlexItem>
              <FieldFolderName
                autoFocus={true}
                value={fields.name.value}
                color={fields.color.value}
                colorIsValid={fields.color.isValid}
                onChange={(field) => handleFieldChange("name", field)}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <FieldFolderColor
                value={fields.color.value}
                isValid={fields.color.isValid}
                onChange={(field) => handleFieldChange("color", field)}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          onClick={() => void handleSubmit()}
          disabled={Object.values(fields).some((field) => !field.isValid)}
        >
          Create
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
